import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useRef, useState, useEffect } from 'react';
import { api } from '../lib/api.js';

export interface EmailMessage {
  id: number;
  channelId: number;
  direction: string;
  subject: string | null;
  body: string | null;
  classifiedAs: string | null;
  classificationSource: string | null;
  classificationConfidence: number | null;
  timestamp: string;
  processedAt: string | null;
  fromEmail: string | null;
  accountName: string | null;
  isRead: number;
  needsFollowup: number;
  followupReason: string | null;
  followupPriority: string | null;
  followupDecision: string | null;
  followupReasoning: string | null;
  followupConfidence: number | null;
  followupAnalyzedAt: string | null;
  deletedAt: string | null;
  autoDeleteAt: string | null;
}

export interface MessagesResult {
  rows: EmailMessage[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface EmailFilters {
  category?: string;
  account?: string;
  search?: string;
  unclassified?: boolean;
  unread?: boolean;
  needsFollowup?: boolean;
  followupDecision?: string;
  followupPriority?: string;
  today?: boolean;
  trash?: boolean;
  hideSpam?: boolean;
  page?: number;
  limit?: number;
}

function buildMessageParams(filters: Omit<EmailFilters, 'page'>): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.category) params.set('category', filters.category);
  if (filters.account) params.set('account', filters.account);
  if (filters.search) params.set('search', filters.search);
  if (filters.unclassified) params.set('unclassified', 'true');
  if (filters.unread) params.set('unread', 'true');
  if (filters.hideSpam) params.set('hideSpam', 'true');
  if (filters.needsFollowup) params.set('needsFollowup', 'true');
  if (filters.followupDecision) params.set('followupDecision', filters.followupDecision);
  if (filters.followupPriority) params.set('followupPriority', filters.followupPriority);
  if (filters.today) params.set('today', 'true');
  if (filters.trash) params.set('trash', 'true');
  if (filters.limit) params.set('limit', String(filters.limit));
  return params;
}

export function useMessages(filters: EmailFilters = {}) {
  const params = buildMessageParams(filters);
  if (filters.page) params.set('page', String(filters.page));
  return useQuery<MessagesResult>({
    queryKey: ['email-messages', filters],
    queryFn: () => api.get<MessagesResult>(`/api/email/messages?${params.toString()}`),
  });
}

// Cursor-based infinite scroll version. Key starts with ['email-messages'] so
// existing invalidateQueries({ queryKey: ['email-messages'] }) clears it too.
export function useInfiniteMessages(filters: Omit<EmailFilters, 'page'> = {}) {
  const baseFilters = filters;
  return useInfiniteQuery<MessagesResult, Error, { pages: MessagesResult[] }, unknown[], string | undefined>({
    queryKey: ['email-messages', 'infinite', baseFilters],
    queryFn: ({ pageParam }) => {
      const params = buildMessageParams(baseFilters);
      if (pageParam) params.set('cursor', pageParam);
      return api.get<MessagesResult>(`/api/email/messages?${params.toString()}`);
    },
    getNextPageParam: (lastPage) => {
      const limit = filters.limit ?? 50;
      if (lastPage.rows.length < limit) return undefined;
      const last = lastPage.rows[lastPage.rows.length - 1];
      return last?.timestamp;
    },
    initialPageParam: undefined,
  });
}

export interface ThreadSummary {
  id: number;
  accountName: string;
  subjectNormalized: string;
  subjectDisplay: string | null;
  lastMessageAt: string;
  firstMessageAt: string;
  messageCount: number;
  hasUnread: number;
  lastMessagePreview: string | null;
  lastFrom: string | null;
  threadCategory: string | null;
}

export interface ThreadsResult {
  threads: ThreadSummary[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ThreadDetail {
  thread: {
    id: number;
    accountName: string;
    subjectNormalized: string;
    subjectDisplay: string | null;
    firstMessageAt: string;
    lastMessageAt: string;
    messageCount: number;
    hasUnread: number;
  };
  messages: EmailMessage[];
}

export interface ThreadFilters {
  account?: string;
  search?: string;
  unread?: boolean;
  hideSpam?: boolean;
  page?: number;
  limit?: number;
}

export function useThreads(filters: ThreadFilters = {}) {
  const params = new URLSearchParams();
  if (filters.account) params.set('account', filters.account);
  if (filters.search) params.set('search', filters.search);
  if (filters.unread) params.set('unread', 'true');
  if (filters.hideSpam) params.set('hideSpam', 'true');
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  return useQuery<ThreadsResult>({
    queryKey: ['email-threads', filters],
    queryFn: () => api.get<ThreadsResult>(`/api/email/threads?${params.toString()}`),
  });
}

export function useThreadDetail(id: number | null) {
  return useQuery<ThreadDetail>({
    queryKey: ['email-thread', id],
    queryFn: () => api.get<ThreadDetail>(`/api/email/threads/${id}`),
    enabled: id !== null,
  });
}

export function useEmailCategories() {
  return useQuery<Array<{ category: string; count: number }>>({
    queryKey: ['email-categories'],
    queryFn: () => api.get('/api/email/messages/categories'),
    staleTime: 30_000,
  });
}

export function useClassifyMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<EmailMessage>(`/api/email/messages/${id}/classify`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-categories'] });
      void qc.invalidateQueries({ queryKey: ['email-uncategorized'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function useBulkCategorizeMessages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, category }: { ids: number[]; category: string }) =>
      api.post('/api/email/messages/bulk/categorize', { ids, category }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-categories'] });
      void qc.invalidateQueries({ queryKey: ['email-uncategorized'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function useEmailAccounts() {
  return useQuery<string[]>({
    queryKey: ['email-accounts'],
    queryFn: () => api.get('/api/email/accounts'),
    staleTime: 60_000,
  });
}

export function useSyncAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ account, useClaude }: { account: string; useClaude?: boolean }) =>
      api.post(`/api/email/sync/${account}`, { useClaude: useClaude ?? false, cutoffDays: 3 }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-categories'] });
      void qc.invalidateQueries({ queryKey: ['email-uncategorized'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export interface EmailStats {
  pendingFollowup: number;
  unreadCount: number;
  totalMessages: number;
  activeRules: number;
  lastRunMoved: number;
  lastRun: { id: number; status: string; startedAt: string; finishedAt: string | null; type: string } | null;
}

export function useEmailStats() {
  return useQuery<EmailStats>({
    queryKey: ['email-stats'],
    queryFn: () => api.get<EmailStats>('/api/email/stats'),
    refetchInterval: 30_000,
  });
}

export interface MessageDetail {
  id: number;
  channelId: number;
  subject: string | null;
  body: string | null;
  classifiedAs: string | null;
  timestamp: string;
  fromEmail: string | null;
  account: string;
  domain: string;
  isRead: boolean;
  needsFollowup: number;
  rfcMessageId: string | null;
  direction: string;
  contactId: number | null;
  contactDecision: string | null;
  followupDecision?: string | null;
  followupPriority?: string | null;
  followupReasoning?: string | null;
  followupConfidence?: number | null;
  followupAnalyzedAt?: string | null;
  followupManuallySet?: boolean;
  followupReason?: string | null;
}

export interface DetailResult {
  detail: MessageDetail;
  thread: MessageDetail[];
}

export function useEmailDetail(id: number | null) {
  return useQuery<DetailResult>({
    queryKey: ['email-detail', id],
    queryFn: () => api.get<DetailResult>(`/api/email/messages/${id}/detail`),
    enabled: id != null && id > 0,
  });
}

export function useSendComposeChat() {
  return useMutation({
    mutationFn: (payload: {
      message: string;
      recipientEmail: string;
      contactName?: string;
      draft?: { subject?: string; body?: string };
      history: Array<{ role: 'user' | 'assistant'; content: string }>;
    }) => api.post<{ reply: string }>('/api/email/compose-chat', payload),
  });
}

export interface RunRow {
  id: number;
  type: string;
  trigger: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  itemsProcessed: number;
  itemsActioned: number;
}

export function useEmailRuns(limit = 20) {
  return useQuery<RunRow[]>({
    queryKey: ['email-runs', limit],
    queryFn: () => api.get<RunRow[]>(`/api/email/runs?limit=${limit}`),
    refetchInterval: 15_000,
  });
}

export type ScanPhase = 'idle' | 'running' | 'done' | 'error';

interface ScanStatus {
  status: string;
  processed: number;
  actioned: number;
  error: string | null;
}

export function useTriggerScan() {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<ScanPhase>('idle');
  const [toast, setToast] = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  function finish(p: 'done' | 'error', msg: string) {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    attemptsRef.current = 0;
    setPhase(p);
    setToast(msg);
    setTimeout(() => { setPhase('idle'); setToast(''); }, p === 'done' ? 2000 : 4000);
  }

  function startPolling(runId: number) {
    attemptsRef.current = 0;
    intervalRef.current = setInterval(() => {
      attemptsRef.current++;
      if (attemptsRef.current > 60) { finish('error', 'El scan está tardando más de lo normal. Puede haber completado en background — recarga la lista en unos segundos para verificar.'); return; }
      api.get<ScanStatus>(`/api/email/scan/status/${runId}`)
        .then(s => {
          if (s.status === 'success') {
            void qc.invalidateQueries({ queryKey: ['email-messages'] });
            void qc.invalidateQueries({ queryKey: ['email-stats'] });
            void qc.invalidateQueries({ queryKey: ['email-runs'] });
            void qc.invalidateQueries({ queryKey: ['email-categories'] });
            void qc.invalidateQueries({ queryKey: ['email-uncategorized'] });
            void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
            finish('done', `Scan completado: ${s.processed} emails procesados`);
          } else if (s.status === 'error') {
            finish('error', s.error ?? 'Error desconocido');
          }
        })
        .catch(() => { /* keep polling */ });
    }, 1000);
  }

  async function trigger() {
    if (phase !== 'idle') return;
    setPhase('running');
    setToast('');
    try {
      const result = await api.post<{ runId: number; accounts: string[] }>('/api/email/scan/run');
      void qc.invalidateQueries({ queryKey: ['email-runs'] });
      startPolling(result.runId);
    } catch (err) {
      finish('error', err instanceof Error ? err.message : String(err));
    }
  }

  return { trigger, phase, toast };
}

export function useBackfillFollowup() {
  const qc = useQueryClient();
  return useMutation<{ processed: number; flagged: number }, Error, boolean>({
    mutationFn: (useClaude: boolean) =>
      api.post<{ processed: number; flagged: number }>('/api/email/followup/backfill', { useClaude }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation<{ affectedChannels: number; affectedMessages: number } | { ok: boolean; id: number; category: string }, Error, { id: number; category: string; fromEmail?: string | null | undefined }>({
    mutationFn: ({ id, category, fromEmail }) => {
      if (fromEmail) {
        return api.post<{ affectedChannels: number; affectedMessages: number }>('/api/sender/categorize', { senderEmail: fromEmail, category });
      }
      return api.put<{ ok: boolean; id: number; category: string }>(`/api/email/messages/${id}/category`, { category });
    },
    onMutate: async ({ id, category }) => {
      await qc.cancelQueries({ queryKey: ['email-messages'] });
      await qc.cancelQueries({ queryKey: ['email-detail', id] });
      const prev = qc.getQueriesData({ queryKey: ['email-messages'] });
      const prevDetail = qc.getQueryData<DetailResult>(['email-detail', id]);
      qc.setQueriesData({ queryKey: ['email-messages'] }, (old: MessagesResult | undefined) => {
        if (!old) return old;
        return { ...old, rows: old.rows.map(r => r.id === id ? { ...r, classifiedAs: category } : r) };
      });
      // Optimistically update the detail panel header so category reflects instantly
      qc.setQueryData<DetailResult>(['email-detail', id], (old) => {
        if (!old) return old;
        return { ...old, detail: { ...old.detail, classifiedAs: category || null } };
      });
      return { prev, prevDetail };
    },
    onError: (_err, { id }, ctx) => {
      const context = ctx as { prev: Array<[unknown, MessagesResult | undefined]>; prevDetail: DetailResult | undefined } | undefined;
      context?.prev.forEach(([key, val]) => qc.setQueryData(key as Parameters<typeof qc.setQueryData>[0], val));
      if (context?.prevDetail !== undefined) qc.setQueryData<DetailResult>(['email-detail', id], context.prevDetail);
    },
    onSettled: (_data, _err, { id }) => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-detail', id] });
      void qc.invalidateQueries({ queryKey: ['email-threads'] });
      void qc.invalidateQueries({ queryKey: ['email-categories'] });
      void qc.invalidateQueries({ queryKey: ['email-uncategorized'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function useUncategorizedEmails(limit = 5) {
  return useQuery<MessagesResult>({
    queryKey: ['email-uncategorized'],
    queryFn: () => api.get<MessagesResult>(`/api/email/messages?unclassified=true&limit=${limit}`),
    staleTime: 30_000,
  });
}

export function useBulkTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.post<{ trashed: number }>('/api/email/messages/bulk/trash', { ids }),
    onMutate: async (ids: number[]) => {
      await qc.cancelQueries({ queryKey: ['email-messages'] });
      const prev = qc.getQueriesData<MessagesResult>({ queryKey: ['email-messages'] });
      qc.setQueriesData<MessagesResult>({ queryKey: ['email-messages'] }, old => {
        if (!old) return old;
        const filtered = old.rows.filter(r => !ids.includes(r.id));
        return { ...old, rows: filtered, total: old.total - (old.rows.length - filtered.length) };
      });
      return { prev };
    },
    onError: (_err, _ids, ctx) => {
      const context = ctx as { prev: Array<[unknown, MessagesResult | undefined]> } | undefined;
      context?.prev.forEach(([key, val]) => qc.setQueryData(key as Parameters<typeof qc.setQueryData>[0], val));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-threads'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function useBulkRestore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.post<{ restored: number }>('/api/email/messages/bulk/restore', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function usePermanentDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.delete<{ deleted: number }>('/api/email/messages/bulk/permanent', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function useEmptyTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ deleted: number }>('/api/email/messages/trash/empty'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function useBulkThreadAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadIds, action, value }: { threadIds: number[]; action: 'trash' | 'mark_read' | 'mark_unread' | 'set_category'; value?: string }) =>
      api.post<{ affected: number }>('/api/email/threads/bulk', { threadIds, action, value }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-threads'] });
      void qc.invalidateQueries({ queryKey: ['email-thread'] });
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

// ── Follow-up override + sender rules (FEATURE #7) ────────────────────────────

export function useToggleFollowup() {
  const qc = useQueryClient();
  return useMutation<{ affectedChannels: number; affectedMessages: number } | { ok: boolean; rule: FollowupRule | null }, Error, { id: number; value: boolean; fromEmail?: string | null; learn?: boolean; learnScope?: 'sender' | 'domain' }>({
    mutationFn: ({ id, value, fromEmail, learn, learnScope }) => {
      if (fromEmail) {
        return api.post<{ affectedChannels: number; affectedMessages: number }>('/api/sender/followup', { senderEmail: fromEmail, isFollowup: value });
      }
      return api.put<{ ok: boolean; rule: FollowupRule | null }>(`/api/email/messages/${id}/followup`, { value, learn, learnScope });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-detail'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['followup-rules'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export interface FollowupRule {
  id: number;
  sender: string | null;
  domain: string | null;
  action: 'always' | 'never';
  createdAt: string;
}

export function useFollowupRules() {
  return useQuery<FollowupRule[]>({
    queryKey: ['followup-rules'],
    queryFn: () => api.get<FollowupRule[]>('/api/email/followup/rules'),
    staleTime: 30_000,
  });
}

export function useCreateFollowupRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sender, domain, action }: { sender?: string; domain?: string; action: 'always' | 'never' }) =>
      api.post<FollowupRule>('/api/email/followup/rules', { sender, domain, action }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['followup-rules'] }); },
  });
}

export function useBulkToggleFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, value }: { ids: number[]; value: boolean }) =>
      api.post<{ updated: number }>('/api/email/messages/bulk/followup', { ids, value }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function usePropagateSenderCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fromEmail, category, scope }: { id: number; fromEmail: string; category: string; scope?: 'sender' | 'domain' }) =>
      api.post<{ count: number }>(`/api/email/messages/${id}/propagate-category`, { fromEmail, category, scope }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-categories'] });
      void qc.invalidateQueries({ queryKey: ['email-uncategorized'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function usePropagation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { fromEmail: string; actionType: 'category' | 'followup'; scope: 'sender' | 'domain'; value: string | boolean }) =>
      api.post<{ updated: number; ruleCreated: boolean }>('/api/email/propagate', params),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-categories'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function useDeleteFollowupRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/email/followup/rules/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['followup-rules'] }); },
  });
}

export function useMarkAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.patch<{ ok: boolean }>(`/api/email/messages/${id}/read`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['email-messages'] });
      qc.setQueriesData({ queryKey: ['email-messages'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        if ('pages' in old && Array.isArray((old as { pages: unknown[] }).pages)) {
          const inf = old as { pages: MessagesResult[]; pageParams: unknown[] };
          return {
            ...inf,
            pages: inf.pages.map(p => ({
              ...p,
              rows: p.rows.map(r => r.id === id ? { ...r, isRead: 1 } : r),
            })),
          };
        }
        if ('rows' in old) {
          const flat = old as MessagesResult;
          return { ...flat, rows: flat.rows.map(r => r.id === id ? { ...r, isRead: 1 } : r) };
        }
        return old;
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-detail'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['email-threads'] });
      void qc.invalidateQueries({ queryKey: ['email-thread'] });
    },
  });
}

export function useMarkAsUnread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.patch<{ ok: boolean }>(`/api/email/messages/${id}/unread`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['email-messages'] });
      qc.setQueriesData({ queryKey: ['email-messages'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        if ('pages' in old && Array.isArray((old as { pages: unknown[] }).pages)) {
          const inf = old as { pages: MessagesResult[]; pageParams: unknown[] };
          return {
            ...inf,
            pages: inf.pages.map(p => ({
              ...p,
              rows: p.rows.map(r => r.id === id ? { ...r, isRead: 0 } : r),
            })),
          };
        }
        if ('rows' in old) {
          const flat = old as MessagesResult;
          return { ...flat, rows: flat.rows.map(r => r.id === id ? { ...r, isRead: 0 } : r) };
        }
        return old;
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-detail'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['email-threads'] });
      void qc.invalidateQueries({ queryKey: ['email-thread'] });
    },
  });
}

// ── Compose (Reply / Reply All / Forward) ─────────────────────────────────────

export interface ComposeDraft {
  accountName: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  quotedHtml?: string;
  inReplyTo: string | null;
  references: string[];
  replyToMessageId: number | null;
}

export type ComposeType = 'reply' | 'reply-all' | 'forward' | 'compose';

export function useComposeDraft() {
  return useMutation({
    mutationFn: ({ id, type }: { id: number; type: ComposeType }) => {
      const path =
        type === 'reply' ? 'compose-reply'
        : type === 'reply-all' ? 'compose-reply-all'
        : 'compose-forward';
      return api.post<ComposeDraft>(`/api/email/messages/${id}/${path}`);
    },
  });
}

export interface AttachmentMeta {
  id: number;
  filename: string;
  sizeBytes: number;
  mimeType: string;
}

export function useUploadAttachment() {
  return useMutation({
    mutationFn: (formData: FormData) =>
      api.upload<AttachmentMeta>('/api/email/attachments', formData),
  });
}

export function useDeleteAttachment() {
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/email/attachments/${id}`),
  });
}

export function useDraftAttachments(draftId: number | null) {
  return useQuery<AttachmentMeta[]>({
    queryKey: ['draft-attachments', draftId],
    queryFn: () => api.get<AttachmentMeta[]>(`/api/email/drafts/${draftId}/attachments`),
    enabled: draftId != null && draftId > 0,
  });
}

export function useMessageAttachments(messageId: number | null) {
  return useQuery<AttachmentMeta[]>({
    queryKey: ['message-attachments', messageId],
    queryFn: () => api.get<AttachmentMeta[]>(`/api/email/messages/${messageId}/attachments`),
    enabled: messageId != null && messageId > 0,
  });
}

export interface SendEmailPayload {
  accountName: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  html?: string;
  text?: string;
  inReplyTo?: string | null;
  references?: string[];
  replyToMessageId?: number | null;
  attachmentIds?: number[];
}

export interface SendEmailResult {
  messageId: string;
  accepted: string[];
  rejected: string[];
  dbMessageId: number | null;
  channelId: number | null;
}

export function useSendEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendEmailPayload) =>
      api.post<SendEmailResult>('/api/email/send', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-sent'] });
    },
  });
}

export function useSentMessages(filters: { page?: number; limit?: number } = {}) {
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  return useQuery<MessagesResult>({
    queryKey: ['email-sent', filters],
    queryFn: () => api.get<MessagesResult>(`/api/email/sent?${params.toString()}`),
  });
}

export interface DraftRow {
  id: number;
  accountName: string;
  toAddresses: string | null;
  ccAddresses: string | null;
  bccAddresses: string | null;
  subject: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  inReplyTo: string | null;
  referencesHeader: string | null;
  replyToMessageId: number | null;
  createdAt: string;
  updatedAt: string;
}

export function useDrafts() {
  return useQuery<DraftRow[]>({
    queryKey: ['email-drafts'],
    queryFn: () => api.get<DraftRow[]>('/api/email/drafts'),
  });
}

export function useSaveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draft: Partial<DraftRow> & { accountName: string }) =>
      api.post<DraftRow>('/api/email/drafts', draft),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['email-drafts'] }); },
  });
}

export function useUpdateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<DraftRow> & { id: number }) =>
      api.put<DraftRow>(`/api/email/drafts/${id}`, data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['email-drafts'] }); },
  });
}

export function useDiscardDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/email/drafts/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['email-drafts'] }); },
  });
}

export interface SearchResult {
  id: number;
  direction: string;
  subject: string | null;
  snippet: string;
  sender: string;
  timestamp: string;
}

export function useSearchEmails(q: string) {
  return useQuery<SearchResult[]>({
    queryKey: ['email-search', q],
    queryFn: () => api.get<SearchResult[]>(`/api/email/search?q=${encodeURIComponent(q)}&limit=20`),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });
}

export interface FollowupAnalysisResult {
  success: boolean;
  decision?: string;
  priority?: string | null;
  reasoning?: string;
  confidence?: number;
  skipped?: string;
}

export function useReanalyzeFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<FollowupAnalysisResult>(`/api/email/messages/${id}/reanalyze-followup`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-detail'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
    },
  });
}

export function useUpdateFollowupManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) =>
      api.put<{ ok: boolean; rule: FollowupRule | null }>(`/api/email/messages/${id}/followup`, {
        value,
        learn: false,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-detail'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

export function useFollowupDecisionOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, priority }: { id: number; decision: 'followup_yes' | 'followup_no' | 'followup_uncertain'; priority?: string }) =>
      api.post<{ ok: boolean }>(`/api/email/messages/${id}/followup-override`, { decision, priority }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-messages'] });
      void qc.invalidateQueries({ queryKey: ['email-detail'] });
      void qc.invalidateQueries({ queryKey: ['email-stats'] });
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}
